// ./src/components/generic/MediaSelector/stockShape.ts
//
// ROADMAP A2e #95 — the picture SHAPE the stock search asks for (Any / Vertical / Horizontal / Square). Pure and free of
// `import.meta.env`, so `check-stock-media` RUNS it; `stockMedia.ts` re-exports it.
import type { StockSource } from "./stockMedia";

/**
 * ROADMAP A2e #95 — the SHAPE of picture asked for. Each service names it its own way (⚠ Unsplash's square is
 * `squarish`, Pexels' is `square`), and GIPHY has no such filter (its tab offers none).
 */
export type StockShape = "any" | "portrait" | "landscape" | "square";

export const STOCK_SHAPES: StockShape[] = ["any", "portrait", "landscape", "square"];

const ORIENTATION: Record<StockSource, Partial<Record<StockShape, string>> | null> = {
  unsplash: { portrait: "portrait", landscape: "landscape", square: "squarish" },
  pexels: { portrait: "portrait", landscape: "landscape", square: "square" },
  giphy: null,
};

/** Whether this service can filter by shape. */
export const stockShapeSupported = (source: StockSource): boolean => ORIENTATION[source] != null;

/** The query parameter for a shape on this service ("&orientation=…"), or "" for any shape / a service without one. */
export const orientationParam = (source: StockSource, shape: StockShape): string => {
  const value = ORIENTATION[source]?.[shape];

  return value ? `&orientation=${value}` : "";
};

/** The shape a picker opens on, from the crop its use asks for (a 16:9 banner wants a wide picture). */
export const shapeForCrop = (cropAspect: string | null | undefined): StockShape => {
  if (cropAspect === "square") {
    return "square";
  }

  if (cropAspect === "16:9" || cropAspect === "3:2" || cropAspect === "4:3" || cropAspect === "2:1") {
    return "landscape";
  }

  return "any";
};


/** A result's width ÷ height, clamped to 1:2 … 2:1 so one panorama or one tall strip cannot dominate; 1 when unknown. */
export const tileRatio = (width: number | null | undefined, height: number | null | undefined): number => {
  const w = Number(width);
  const h = Number(height);

  if (!(w > 0) || !(h > 0)) {
    return 1;
  }

  return Math.min(2, Math.max(0.5, w / h));
};

/**
 * #95 — the results dealt into `columns` columns, each to the one that is SHORTEST so far (the leftmost on a tie), in
 * order. ⚠ Deterministic and append-stable: "Load more" adds to the end and never moves a tile already placed (CSS
 * columns re-balance everything, so the pictures the host was looking at jumped).
 */
export const masonryColumns = <T>(items: T[], columns: number, ratioOf: (item: T) => number): T[][] => {
  const n = Math.max(1, Math.floor(columns) || 1);
  const out: T[][] = Array.from({ length: n }, () => []);
  const heights = new Array<number>(n).fill(0);

  for (const item of items) {
    let target = 0;

    for (let c = 1; c < n; c += 1) {
      if (heights[c] < heights[target] - 1e-9) {
        target = c;
      }
    }

    out[target].push(item);
    heights[target] += 1 / ratioOf(item);
  }

  return out;
};
